import React from "react";
import { motion } from "framer-motion";
import {
  HelpCircle,
  FileText,
  Upload,
  Users,
  Download,
  CheckCircle,
  AlertCircle,
} from "lucide-react";

const ContactHelpSection = ({ onDownloadTemplate }) => {
  const helpItems = [
    {
      icon: FileText,
      title: "File Formats",
      description: "We support XLS and XLSX files up to 5MB",
      color: "blue",
    },
    {
      icon: Users,
      title: "Contact Fields",
      description:
        "Include Name, Phone, Email (optional), and other custom fields",
      color: "red",
    },
    {
      icon: Upload,
      title: "Group Organization",
      description: "Organize contacts into groups for targeted messaging",
      color: "purple",
    },
    {
      icon: Download,
      title: "Request Export",
      description: "Request for your contact groups as XLSX files",
      color: "orange",
    },
  ];

  const tips = [
    {
      type: "success",
      title: "Pro Tip",
      message:
        "Use our XLSX template to ensure proper formatting and avoid upload errors.",
    },
    {
      type: "info",
      title: "Large Files",
      message:
        "Files over 5MB are processed in the background for optimal performance.",
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm sm:rounded-2xl sm:p-6 lg:p-8"
    >
      {/* Header */}
      <div className="flex gap-3 items-center mb-4 sm:mb-6">
        <div className="flex justify-center items-center w-8 h-8 bg-blue-100 rounded-lg sm:w-10 sm:h-10">
          <HelpCircle className="w-4 h-4 text-blue-600 sm:w-5 sm:h-5" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900 sm:text-xl">
            Contact Management Help
          </h2>
          <p className="text-sm text-gray-600 sm:text-base">
            Tips and best practices for managing your contacts
          </p>
        </div>
      </div>

      {/* Help Items Grid */}
      <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-2 lg:grid-cols-4 sm:gap-6 sm:mb-8">
        {helpItems.map((item, index) => {
          const Icon = item.icon;
          const colorClasses = {
            blue: "bg-blue-50 text-blue-600",
            red: "bg-red-50 text-red-600",
            purple: "bg-purple-50 text-purple-600",
            orange: "bg-orange-50 text-orange-600",
          };

          return (
            <div key={index} className="p-3 text-center sm:p-4">
              <div
                className={`w-10 h-10 sm:w-12 sm:h-12 ${
                  colorClasses[item.color]
                } rounded-lg flex items-center justify-center mx-auto mb-3`}
              >
                <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <h3 className="mb-1 text-sm font-medium text-gray-900 sm:text-base sm:mb-2">
                {item.title}
              </h3>
              <p className="text-xs leading-relaxed text-gray-600 sm:text-sm">
                {item.description}
              </p>
            </div>
          );
        })}
      </div>

      {/* Tips Section */}
      <div className="mb-6 space-y-3 sm:space-y-4 sm:mb-8">
        {tips.map((tip, index) => (
          <div
            key={index}
            className={`p-3 sm:p-4 rounded-lg border ${
              tip.type === "success"
                ? "bg-red-50 border-red-200"
                : "bg-blue-50 border-blue-200"
            }`}
          >
            <div className="flex gap-3 items-start">
              {tip.type === "success" ? (
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1 min-w-0">
                <h4
                  className={`text-sm sm:text-base font-medium mb-1 ${
                    tip.type === "success" ? "text-red-800" : "text-blue-800"
                  }`}
                >
                  {tip.title}
                </h4>
                <p
                  className={`text-xs sm:text-sm ${
                    tip.type === "success" ? "text-red-700" : "text-blue-700"
                  }`}
                >
                  {tip.message}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
        <button
          onClick={onDownloadTemplate}
          className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-red-600 hover:bg-red-500 text-white rounded-lg sm:rounded-xl transition-colors duration-200 font-medium text-sm sm:text-base"
        >
          <Download className="w-4 h-4" />
          <span className="hidden sm:inline">Download XLSX Template</span>
          <span className="sm:hidden">Get Template</span>
        </button>

        <button
          onClick={() => (window.location.href = "/contacts/upload")}
          className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 border border-gray-300 text-gray-700 rounded-lg sm:rounded-xl hover:bg-gray-50 transition-colors duration-200 font-medium text-sm sm:text-base"
        >
          <Upload className="w-4 h-4" />
          <span className="hidden sm:inline">Upload New Contacts</span>
          <span className="sm:hidden">Upload Contacts</span>
        </button>
      </div>
    </motion.div>
  );
};

export default ContactHelpSection;
